import { useState, useEffect } from 'react';

function calcTopLeftPosition(anchor, anchoredElement, placement, spacing) {
    const anchorCss = window.getComputedStyle(anchor, null);
    const anchoredElementCss = window.getComputedStyle(anchoredElement, null);
    const c_top = anchor.offsetTop || 0;
    const c_left = anchor.offsetLeft || 0;
    const c_width = parseInt(anchorCss.getPropertyValue('width'), 10) || 0;
    const c_height = parseInt(anchorCss.getPropertyValue('height'), 10) || 0;
    const t_width = parseInt(anchoredElementCss.getPropertyValue('width'), 10) || 0;
    const t_height = parseInt(anchoredElementCss.getPropertyValue('height'), 10) || 0;
    let left, top;
    if (placement === 'top' || placement === 'bottom') {
        left = c_left + (c_width >> 1) - (t_width >> 1);
        if (placement === 'top')
            top = c_top - t_height - spacing;
        else
            top = c_top + c_height + spacing;
    }
    else if (placement === 'right' || placement === 'left') {
        top = c_top + (c_height >> 1) - (t_height >> 1);
        if (placement === 'left')
            left = c_left - t_width - spacing;
        else
            left = c_left + c_width + spacing;
    }
    return [top, left];
}

function createLocalization() {
    const dict = {};
    return {
        addDefault: (data) => {
            dict['en-us'] = Object.assign(dict['en-us'] || {}, data);
        },
        addLanguage: (lang, data) => {
            const normalizedLang = lang.toLowerCase();
            dict[normalizedLang] = { ...dict[normalizedLang], ...data };
        },
        getLanguage: (_lang) => {
            const lang = String(_lang).toLowerCase();
            if (lang in dict)
                return dict[lang];
            const shortLang = lang.slice(0, 2);
            if (shortLang in dict)
                return dict[shortLang];
            return dict['en-us'];
        },
        translateWithParams: (valueWithParams, params) => {
            let value = valueWithParams;
            if (value) {
                Object.keys(params).map(param => {
                    const repStr = params[param];
                    const regex = new RegExp(`{{${param}}}`, 'g');
                    value = value.replace(regex, repStr);
                });
            }
            return value;
        },
    };
}

function existsInTree(root, node) {
    return root.contains(node);
}

/**
 * web-portal localization interface
 *
 */
let currentLocale;
const LOCALES = [
    'en',
    'de',
    'nl',
    'en-US',
    'en-CA',
    'en-GB',
    'en-IE',
    'en-AR',
    'en-AU',
    'en-SG',
    'es-AR',
    'de-AT',
    'fr-CA',
    'nl-NL',
    'es-MX',
    'pt-BR',
];
LOCALES.reduce((ret, value) => {
    const [locale, country] = value.split('-');
    ret[value] = [locale, (country !== null && country !== void 0 ? country : locale).toUpperCase()].join('');
    return ret;
}, {});
function setCurrentLocale(locale) {
    currentLocale = locale;
}
function getCurrentLocale() {
    return currentLocale;
}

// These values are based off of current values in _breakpoint.scss
const _getDeviceConfig = (width) => {
    if (width < 320) {
        return 'xs';
    }
    else if (width >= 320 && width < 600) {
        return 'sm';
    }
    else if (width >= 600 && width < 900) {
        return 'md';
    }
    else if (width >= 1200) {
        return 'lg';
    }
};
const useBreakpoint = () => {
    const [brkPnt, setBrkPnt] = useState(() => _getDeviceConfig(window.innerWidth));
    useEffect(() => {
        const calcInnerWidth = () => setBrkPnt(_getDeviceConfig(window.innerWidth));
        window.addEventListener('resize', calcInnerWidth);
        return () => window.removeEventListener('resize', calcInnerWidth);
    }, []);
    return brkPnt;
};
const _getDeviceConfigForTable = (width) => {
    if (width < 361) {
        return 'xxs';
    }
    else if (width >= 361 && width <= 600) {
        return 'xs';
    }
    else if (width > 600 && width <= 900) {
        return 'sm';
    }
    else if (width > 900 && width <= 1199) {
        return 'md';
    }
    else if (width > 1200 && width <= 1800) {
        return 'lg';
    }
    else if (width > 1800) {
        return 'xlg';
    }
};
const useTableBreakpoint = () => {
    const [brkPnt, setBrkPnt] = useState(() => _getDeviceConfigForTable(window.innerWidth));
    useEffect(() => {
        const calcInnerWidth = () => setBrkPnt(_getDeviceConfigForTable(window.innerWidth));
        window.addEventListener('resize', calcInnerWidth);
        return () => window.removeEventListener('resize', calcInnerWidth);
    }, []);
    return brkPnt;
};

/**
 * Generates a random string to create an ID. Mainly used in ref objects
 */
function useCreateId() {
    return Math.random().toString(36).slice(2);
}

function useIcon(icon, iconMap) {
    return iconMap[icon];
}

const useIsPopoverNotSupported = (isWithinSpecifiedElement) => {
    const [isNonOptimal, setIsNonOptimal] = useState(false);
    useEffect(() => {
        if (!isWithinSpecifiedElement)
            return;
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        if (isIOS) {
            const match = userAgent.match(/OS\s([0-9]+)_([0-9]+)/);
            if (match) {
                const majorVersion = parseInt(match[1], 10);
                if (majorVersion < 17) {
                    setIsNonOptimal(true);
                }
            }
        }
        else {
            const androidMatch = userAgent.match(/Android\s([0-9.]*)/);
            if (androidMatch) {
                const androidVersion = parseFloat(androidMatch[1]);
                if (androidVersion <= 10) {
                    setIsNonOptimal(true);
                }
            }
        }
    }, [isWithinSpecifiedElement]);
    return isNonOptimal;
};

const focusTrap = (ref, closeFunction) => (e) => {
    var _a;
    if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return closeFunction(e);
    }
    if (e.key !== 'Tab')
        return;
    // here we query all focusable elements, customize as your own need
    const focusableElements = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)');
    if (focusableElements) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        // if going forward by pressing tab and lastElement is active shift focus to first focusable element
        if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement.focus();
            return e.preventDefault();
        }
        // if going backward by pressing tab and firstElement is active shift focus to last focusable element
        if (e.shiftKey && document.activeElement === firstElement) {
            lastElement.focus();
            return e.preventDefault();
        }
    }
};

/**
 * Checks if `showModal` is supported in the current environment.
 *
 * @returns {boolean} - Returns `true` if `HTMLDialogElement.showModal` is available.
 */
const dialogSupported = () => {
    return typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function';
};

/**
 * Normalizes a date string by adding leading zeros where necessary based on the locale's date format.
 *
 * This function handles various date formats (e.g., 'mm/dd/yyyy', 'dd/mm/aaaa', 'tt.mm.jjjj', 'aaaa-mm-jj', etc.)
 * and ensures that the month, day, and year components are padded correctly with leading zeros. It also accounts
 * for different delimiters such as '/', '.', and '-' depending on the format.
 *
 * @param value // The date string entered by the user (e.g., '1/2/1983' or '01/02/1983')
 * @param formatLabel // The expected date format label based on the locale (e.g., 'mm/dd/yyyy', 'dd/mm/aaaa', etc.)
 * @returns // A normalized date string with proper leading zeros and the appropriate delimiter (e.g., '01/02/1983')
 */
const normalizeDateInput = (value, formatLabel) => {
    var _a, _b, _c, _d, _e, _f;
    // Determine the delimiter based on the format
    const delimiter = formatLabel.includes('/') ? '/' : formatLabel.includes('.') ? '.' : '-';
    // Split the input into parts based on the delimiter
    const parts = value.split(delimiter);
    // Ensure the number of parts matches the expected format (e.g., 3 for full date)
    const expectedParts = formatLabel.split(delimiter).length;
    if (parts.length !== expectedParts) {
        return value; // Return the value as is if the number of parts doesn't match
    }
    // Handle the format 'mm/dd/yyyy', 'dd/mm/aaaa', 'tt.mm.jjjj', or 'aaaa-mm-jj'
    if (formatLabel === 'mm/dd/yyyy' ||
        formatLabel === 'dd/mm/aaaa' ||
        formatLabel === 'tt.mm.jjjj' ||
        formatLabel === 'dd-mm-jjjj' ||
        formatLabel === 'aaaa-mm-jj') {
        const firstPart = (_a = parts[0]) === null || _a === void 0 ? void 0 : _a.padStart(formatLabel.startsWith('aaaa') ? 4 : 2, '0'); // Pad year or month/day
        const secondPart = (_b = parts[1]) === null || _b === void 0 ? void 0 : _b.padStart(2, '0'); // Pad month or day
        const thirdPart = parts[2] ? parts[2].padStart(2, '0') : ''; // Ensure day is 2 digits if available
        return `${firstPart}${delimiter}${secondPart}${delimiter}${thirdPart}`;
    }
    // Handle formats like 'mm/dd', 'dd/mm', etc.
    if (formatLabel === 'mm/dd' ||
        formatLabel === 'dd/mm' ||
        formatLabel === 'tt.mm' ||
        formatLabel === 'dd-mm' ||
        formatLabel === 'mm-jj') {
        const firstPart = (_c = parts[0]) === null || _c === void 0 ? void 0 : _c.padStart(2, '0'); // Pad first part (month or day)
        const secondPart = (_d = parts[1]) === null || _d === void 0 ? void 0 : _d.padStart(2, '0'); // Pad second part (day or month)
        return `${firstPart}${delimiter}${secondPart}`;
    }
    // Handle formats like 'mm/yy', 'mm/aa', etc.
    if (formatLabel === 'mm/yy' || formatLabel === 'mm/aa' || formatLabel === 'mm.jj' || formatLabel === 'mm-aa') {
        const firstPart = (_e = parts[0]) === null || _e === void 0 ? void 0 : _e.padStart(2, '0'); // Pad month
        const secondPart = (_f = parts[1]) === null || _f === void 0 ? void 0 : _f.padStart(2, '0'); // Ensure year is 2 digits (if available)
        return `${firstPart}${delimiter}${secondPart}`;
    }
    return value; // Return the value as-is if no normalization is needed
};

const normalizeTime = (time, timeFormat) => {
    // Regular expression to match 12-hour time with optional AM/PM
    const twelveHourRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/i;
    // Regular expression to match 24-hour time (HH:MM)
    const twentyFourHourRegex = /^([01]?\d|2[0-3]):([0-5]\d)$/;
    if (timeFormat === '12') {
        const match = time.match(twelveHourRegex);
        if (match) {
            let [hour, , period] = [match[1], match[2], match[3]]; // `hour` and `period` use `let` as they might be reassigned
            const minute = match[2]; // `minute` is set as `const` since it’s not reassigned
            // Normalize hour to 2 digits in 12-hour format
            hour = hour.padStart(2, '0');
            // If period exists (AM/PM), ensure there is a space before it and it's uppercase
            if (period) {
                period = period.toUpperCase().trim();
                return `${hour}:${minute} ${period}`;
            }
            return `${hour}:${minute}`; // No AM/PM present, return without adding it
        }
    }
    // If the time format is 24-hour
    if (timeFormat === '24') {
        const match = time.match(twentyFourHourRegex);
        if (match) {
            let hour = match[1];
            const minute = match[2];
            // Ensure hour is always 2 digits in 24-hour format
            hour = hour.padStart(2, '0');
            return `${hour}:${minute}`;
        }
    }
    // If no match, return the original input (you can add more error handling if necessary)
    return time;
};

export { calcTopLeftPosition, createLocalization, dialogSupported, existsInTree, focusTrap, getCurrentLocale, normalizeDateInput, normalizeTime, setCurrentLocale, useBreakpoint, useCreateId, useIcon, useIsPopoverNotSupported, useTableBreakpoint };
//# sourceMappingURL=index.mjs.map
